/**
 * NavContainer Demo Page
 * 
 * Test page to verify the modernized NavContainer component
 */

import React from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, Typography, Paper } from '@mui/material';
import { configureStore } from '@reduxjs/toolkit';
import { lightTheme } from '../../theme';
import MuiNavContainer from '../organisms/NavContainer';
import ToolContext from '../services/ToolContext';

// Mock Redux store for demo
const mockStore = configureStore({
  reducer: {
    ui: (state = {
      showing: 'room',
      currentView: 'dimensions',
      exitToRoom: false,
    }) => state,
    container: (state = {
      project: { name: 'Demo Project' }
    }) => state,
  },
});

// Mock tool context
const mockTool = {
  menus: {
    floorplan: { room: true },
    dimensions: { room: true },
    devices: { room: true },
    coverage: { room: true },
    settings: { room: true },
  },
  constants: {
    tool: {
      fullName: 'Room Designer',
      name: 'room-designer',
      path: { long: 'room-designer' },
    },
  },
};

const NavContainerDemo: React.FC = () => {
  return (
    <Provider store={mockStore}>
      <BrowserRouter>
        <ToolContext.Provider value={mockTool as any}>
          <ThemeProvider theme={lightTheme}>
            <CssBaseline />
            <Box sx={{ display: 'flex', height: '100vh' }}>
              {/* NavContainer on the left */}
              <MuiNavContainer />
              
              {/* Main content area */}
              <Box sx={{ flexGrow: 1, p: 3, backgroundColor: '#f5f5f5' }}>
                <Typography variant="h4" gutterBottom>
                  NavContainer Demo
                </Typography>
                <Typography variant="body1" paragraph>
                  This page demonstrates the modernized NavContainer component with:
                </Typography>
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>Features:</Typography>
                  <ul>
                    <li>Modern Material Design 3 styling</li>
                    <li>Improved navigation with better visual feedback</li>
                    <li>Cleaner Biamp branding</li>
                    <li>Better hover and active states</li>
                    <li>Warning indicators for validation errors</li>
                    <li>Smooth transitions and animations</li>
                  </ul>
                </Paper>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Navigation Items:</Typography>
                  <Typography variant="body2">
                    The navigation shows menu items based on the current context.
                    Click on items to see the active state styling.
                  </Typography>
                </Paper>
              </Box>
            </Box>
          </ThemeProvider>
        </ToolContext.Provider>
      </BrowserRouter>
    </Provider>
  );
};

export default NavContainerDemo;